import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useCalendarNote, NoteType } from '../../../hooks/useCalendarNote';
import useUserContext from '../../../hooks/useUserContext';
import './index.css';

/**
 * Props interface for CalendarNoteEditor component.
 * - type: The type of note (e.g., "event", "task").
 * - date: The date associated with the note.
 * - user: The username of the user.
 */
interface Props {
  type: NoteType;
  date: Date;
  user: string;
}

/**
 * calendarNoteEditor component
 * This component allows users to view and edit notes associated with a specific date and type.
 * It uses the useCalendarNote hook to fetch and save notes.
 */
const CalendarNoteEditor: React.FC<Props> = ({ type, date }) => {
  const { user } = useUserContext();
  const { note, saveNote, loading } = useCalendarNote(user.username, type, date);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);

  // Update the draft content whenever the saved note changes
  useEffect(() => {
    setDraft(note);
  }, [note]);

  // Save the current draft and exit edit mode
  const handleSave = async () => {
    await saveNote(draft);
    setEditing(false);
  };

  const label = `Notes for the ${type}`;

  return (
    <div className='calendar-note-editor'>
      <h3>{label}</h3>
      {editing ? (
        <>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6} />
          <div className='note-buttons'>
            <button onClick={handleSave} disabled={loading}>
              Save
            </button>
            <button onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className='note-block'>
            {note ? <ReactMarkdown>{note}</ReactMarkdown> : <p>No notes added yet.</p>}
          </div>
          <div className='note-edit-button'>
            <button
              className='form-buttons-button'
              onClick={() => {
                setDraft(note);
                setEditing(true);
              }}>
              Edit
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarNoteEditor;
